/**
 * Search input for the audit table.
 *
 * Debounces user input by 300ms before calling onChange — prevents
 * hammering the API on every keystroke. Shows a search icon and a
 * clear button (×) when text is present.
 */
import React, { useEffect, useState } from "react"

import { THEME } from "../../config/theme"

interface Props {
  value: string
  onChange: (next: string) => void
  placeholder?: string
  disabled?: boolean
}

const DEBOUNCE_MS = 300

export default function SearchInput({
  value,
  onChange,
  placeholder = "Search...",
  disabled = false,
}: Props) {
  const [localValue, setLocalValue] = useState(value)

  // Keep local state in sync if external value changes (e.g., clearAll)
  useEffect(() => {
    setLocalValue(value)
  }, [value])

  // Debounce external propagation
  useEffect(() => {
    if (localValue === value) return
    const timer = setTimeout(() => {
      onChange(localValue)
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [localValue, value, onChange])

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: `${THEME.spacing.xs} ${THEME.spacing.md} ${THEME.spacing.xs} 32px`,
    fontSize: 13,
    borderRadius: THEME.borderRadius.md,
    border: `1px solid ${THEME.colors.silver}`,
    backgroundColor: disabled
      ? THEME.colors.neutral.light.background
      : THEME.colors.neutral.light.surface,
    color: THEME.colors.neutral.light.textPrimary,
    fontFamily: THEME.fonts.primary,
    outline: "none",
    boxSizing: "border-box",
  }

  const iconStyle: React.CSSProperties = {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    width: 14,
    height: 14,
    color: THEME.colors.neutral.light.textSecondary,
    pointerEvents: "none",
  }

  const clearButtonStyle: React.CSSProperties = {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    padding: 0,
    width: 18,
    height: 18,
    borderRadius: THEME.borderRadius.pill,
    border: "none",
    backgroundColor: THEME.colors.silver,
    color: THEME.colors.white,
    fontSize: 12,
    lineHeight: 1,
    cursor: "pointer",
    display: localValue ? "flex" : "none",
    alignItems: "center",
    justifyContent: "center",
  }

  return (
    <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
      <svg style={iconStyle} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="7" cy="7" r="5" />
        <path d="M11 11l3 3" strokeLinecap="round" />
      </svg>
      <input
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={inputStyle}
      />
      <button
        type="button"
        onClick={() => {
          setLocalValue("")
          onChange("")
        }}
        style={clearButtonStyle}
        tabIndex={-1}
      >
        ×
      </button>
    </div>
  )
}
